import ReceptionSkeleton from './Reception/ReceptionSkeleton';
import PatientsSkeleton from './Patients/PatientsSkeleton';
import AddPatientSkeleton from './AddPatient/AddPatientSkeleton';
import OperationsSkeleton from './Operations/OperationsSkeleton';
import GoodsSkeleton from './Goods/GoodsSkeleton';
import StoreSkeleton from './Store/StoreSkeleton';
import ReportsSkeleton from './Reports/ReportsSkeleton';
import AccountingSkeleton from './Accounting/AccountingSkeleton';
import PeopleSkeleton from './People/PeopleSkeleton';
import RoleEditorSkeleton from './People/RoleEditorSkeleton';
import SettingsSkeleton from './Settings/SettingsSkeleton';
import ClinicSkeleton from './Clinic/ClinicSkeleton';
import ClinicalSkeleton from './Clinical/ClinicalSkeleton';
import { stripClinic } from '../hooks/useAppNavigate';
import { isGoodsView, isAccountingView, isStoreView } from '../constants/moduleViews';

/**
 * The route → skeleton registry.
 *
 * A lazy route's `Suspense` fallback must be the DESTINATION page's own shape
 * (DESIGN.md §"Loading & skeleton" — navigation), so the transition draws the
 * next page rather than a spinner where a page should be. The same answer
 * serves `ClinicGate` while access is still resolving: the shell is painted,
 * the person has clicked a nav item, and the page they are going to is known
 * from the URL alone.
 *
 * Every skeleton here is imported EAGERLY on purpose — it is what shows while
 * the screen's chunk is in flight, so it cannot live in that chunk. The files
 * are light by contract (kit primitives, tokens, density, i18n only).
 *
 * The legacy paths are mapped to the page they redirect INTO, so a bookmark to
 * `/inventory` draws the items view it is about to land on rather than a
 * placeholder for a page that no longer exists.
 */
const VIEW_ALIASES = {
  '/inventory': ['/goods', 'items'],
  '/expenses': ['/operations', 'expenses'],
  '/services': ['/goods', 'services'],
  '/about': ['/clinic', 'connect'],
  '/subscription': ['/clinic', 'billing'],
  '/calendar': ['/reception', null],
  '/doctors': ['/people', null],
  '/dashboard': ['/reception', null],
};

/** The bare screen path and the `?view=` it should draw, resolved through the aliases. */
export function resolveSkeletonRoute(pathname = '', search = '') {
  let path = stripClinic(pathname);
  let view = new URLSearchParams(search).get('view') || null;
  if (VIEW_ALIASES[path]) {
    const [target, aliasView] = VIEW_ALIASES[path];
    path = target;
    view = aliasView || view;
  }
  // `/clinic/:view` is an alias for `/clinic?view=:view`.
  const clinicView = /^\/clinic\/([^/]+)$/.exec(path);
  if (clinicView) { path = '/clinic'; view = clinicView[1]; }
  // A view that moved to «کالاها و خدمات» in the 2026-09-12 split, asked for at
  // the old address: `App.jsx` is about to forward it, so draw the page it will
  // ARRIVE at. Drawing the expense workbench first would show the operator a
  // ledger skeleton, then a warehouse — a flash of the wrong page, which is
  // exactly what this registry exists to prevent.
  if (path === '/operations' && isGoodsView(view)) path = '/goods';
  // A `?view=` the hub does not know is not drawn as a view at all — the hub
  // normalises it, and guessing a shape here would flash the wrong page.
  if (path === '/accounting' && !isAccountingView(view)) view = null;
  // The same rule for the store, which normalises an unknown `?view=` itself.
  if (path === '/store' && !isStoreView(view)) view = null;
  return { path, view };
}

/**
 * The skeleton element for a location. `null` for a path that has no
 * clinic-scoped page (the pre-membership screens are eager and never suspend).
 */
export function skeletonForPath(pathname, search) {
  const { path, view } = resolveSkeletonRoute(pathname, search);
  const viewProps = view ? { view } : {};

  if (path === '/' || path === '' || path === '/reception') return <ReceptionSkeleton {...viewProps} />;
  if (path === '/patients/new') return <AddPatientSkeleton />;
  // The shared clinical screens (docs/CLINICAL_KIT_DESIGN.md) — before `/patients`,
  // which a record path would otherwise match only by accident of being unequal.
  {
    const record = /^\/patients\/[^/]+\/record(?:\/([^/]+))?$/.exec(path);
    if (record) return <ClinicalSkeleton kind="record" tab={record[1]} />;
    if (/^\/visit\/[^/]+$/.test(path)) return <ClinicalSkeleton kind="visit" />;
  }
  if (path === '/patients') return <PatientsSkeleton {...viewProps} />;
  if (path === '/operations') return <OperationsSkeleton {...viewProps} />;
  if (path === '/goods') return <GoodsSkeleton {...viewProps} />;
  if (path === '/store') return <StoreSkeleton {...viewProps} />;
  if (path === '/reports') return <ReportsSkeleton />;
  if (path === '/accounting') return <AccountingSkeleton {...viewProps} />;
  if (/^\/people\/roles\/[^/]+$/.test(path)) return <RoleEditorSkeleton />;
  if (path === '/people') return <PeopleSkeleton />;
  if (path === '/settings') return <SettingsSkeleton />;
  if (path === '/clinic') return <ClinicSkeleton {...viewProps} />;
  return null;
}

export default skeletonForPath;
